import React from 'react';
import { HomeModernIcon, SearchIcon, PencilIcon, SpinnerIcon } from '../../../components/Icons'; 
import { Property } from '../../../types';
import { CIEN_CUADRAS_BLUE, CIEN_CUADRAS_BUTTON_BLUE } from '../../../firebase/firebaseConfig';
import { formatCurrency } from '../../../utils/formatters';
import { useConnection } from '../../../context/ConnectionContext';


interface AdminPropertyManagementProps {
  propertyCodeAdminInput: string;
  setPropertyCodeAdminInput: (value: string) => void;
  onSearchPropertyAdmin: () => void;
  isLoadingSearch: boolean;
  foundPropertyForAdmin: Property | Property[] | null;
  onEditProperty: (property: Property) => void; // To open modal
  message: string | null; // Messages from parent regarding search/edit operations
}

export const AdminPropertyManagement: React.FC<AdminPropertyManagementProps> = ({
  propertyCodeAdminInput,
  setPropertyCodeAdminInput,
  onSearchPropertyAdmin,
  isLoadingSearch,
  foundPropertyForAdmin,
  onEditProperty,
  message,
}) => {
  const { isOffline } = useConnection();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchPropertyAdmin();
  };

  const renderPropertyDetailCard = (property: Property) => {
    return (
      <div key={property.id} className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
          <h5 className="text-lg font-semibold text-gray-800">
            Inmueble Encontrado: <span style={{color: CIEN_CUADRAS_BLUE}}>{property.codigo}</span>
          </h5>
          <button
            onClick={() => onEditProperty(property)}
            disabled={isOffline}
            className="px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 flex items-center gap-1 disabled:opacity-50"
            style={{ backgroundColor: "#f59e0b" /* Tailwind yellow-500 */}}
          >
            <PencilIcon className="w-4 h-4 mr-1" />
            Editar
          </button>
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div className="col-span-1 md:col-span-2"><dt className="font-medium text-gray-600 block">ID Firestore:</dt><dd className="text-gray-700 break-all font-mono text-xs">{property.id}</dd></div>
          
          <div><dt className="font-medium text-gray-600">Tipo de Inmueble:</dt><dd className="text-gray-900 font-medium">{property.tipoinmueble}</dd></div>
          <div><dt className="font-medium text-gray-600">Ciudad:</dt><dd className="text-gray-700">{property.ciudad}</dd></div>
          
          <div className="col-span-1 md:col-span-2"><dt className="font-medium text-gray-600">Dirección:</dt><dd className="text-gray-700">{property.direccion}</dd></div>
          
          <div><dt className="font-medium text-gray-600">Precio Venta:</dt><dd className="text-gray-700">{formatCurrency(property.precio_venta)}</dd></div>
          <div><dt className="font-medium text-gray-600">Estado App:</dt><dd className={`font-semibold ${property.status === 'Eliminado' ? 'text-red-600' : 'text-green-600'}`}>{property.status}</dd></div>
          
          {property.owner && (
             <div className="col-span-1 md:col-span-2"><dt className="font-medium text-gray-600">Dueño / Propietario:</dt><dd className="text-gray-700">{property.owner}</dd></div>
          )}

          <div className="col-span-1 md:col-span-2 mt-2"><dt className="font-medium text-gray-600">Descripción:</dt><dd className="text-gray-700 line-clamp-3 text-xs italic bg-white p-2 rounded border border-gray-200">{property.descripcion}</dd></div>
        </dl>
      </div>
    );
  };

  return (
    <div className="border-t border-gray-200 pt-6 mt-6">
       <div className="flex items-center text-gray-600 mb-3">
            <HomeModernIcon className="w-6 h-6 mr-2" />
            <h4 className="text-md font-semibold">Buscar y Editar Inmueble</h4>
        </div>
      <p className="text-sm text-gray-600 mb-4">
        Busca un inmueble por su código para ver sus detalles y editarlo directamente en la base de datos de Firestore.
      </p>

      <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 mb-4">
        <label htmlFor="search-property-code" className="sr-only">Código del Inmueble</label>
        <input
          type="text"
          id="search-property-code"
          value={propertyCodeAdminInput}
          onChange={(e) => setPropertyCodeAdminInput(e.target.value)}
          placeholder="Ingrese código del inmueble"
          className="flex-grow block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm disabled:bg-gray-100"
          disabled={isLoadingSearch || isOffline}
        />
        <button
          type="submit"
          disabled={isLoadingSearch || !propertyCodeAdminInput.trim() || isOffline}
          className="w-full sm:w-auto px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 flex items-center justify-center space-x-2 disabled:opacity-50"
          style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
        >
          {isLoadingSearch ? <SpinnerIcon className="w-5 h-5 mr-2" /> : <SearchIcon className="w-5 h-5 mr-2" />}
          <span>{isLoadingSearch ? 'Buscando...' : 'Buscar Inmueble'}</span>
        </button>
      </form>

      {message && !isLoadingSearch && ( // Display messages when not actively searching
        <div className={`my-4 p-3 rounded-md text-sm ${message.toLowerCase().includes('error') ? 'bg-red-100 text-red-700' : (message.toLowerCase().includes('actualizado') || message.toLowerCase().includes('encontrado') ? 'bg-green-100 text-green-700' : 'bg-sky-100 text-sky-700')}`}>
          {message}
        </div>
      )}

      {isLoadingSearch && !foundPropertyForAdmin && (
         <div className="text-center py-6">
            <SpinnerIcon className="w-8 h-8 text-sky-600 mx-auto mb-2" />
            <p className="text-gray-600">Buscando inmueble...</p>
        </div>
      )}

      {foundPropertyForAdmin && !isLoadingSearch && (
        <div className="mt-6 space-y-4 animate-fade-in-down">
          {Array.isArray(foundPropertyForAdmin) ? (
            <>
              <div className="text-xs font-extrabold text-sky-700 uppercase tracking-widest bg-sky-50 px-3 py-2 rounded">
                Múltiples inmuebles encontrados ({foundPropertyForAdmin.length}):
              </div>
              <div className="grid grid-cols-1 gap-4">
                {foundPropertyForAdmin.map(property => renderPropertyDetailCard(property))}
              </div>
            </>
          ) : (
            renderPropertyDetailCard(foundPropertyForAdmin)
          )}
        </div>
      )}
    </div>
  );
};